<template>
  <b-overlay
    class="admin-conversation-detail-wrapper"
    rounded="sm"
    :show="!getReady"
  >
    <div
      class="admin-conversation-detail px-2 px-md-4 py-2 py-md-4"
      :class="{ 'optimized-layout': !isDesktop }"
    >
      <div class="admin-conversation-detail--chat">
        <AdminConversationChatHistory
          v-if="getReady"
          :conversationDetail="conversationDetail"
          :conversationMessages="conversationMessages"
          :conversationAttachments="conversationAttachments"
        />
      </div>
      <div v-if="!isDesktop" class="admin-conversation-detail--mobile-btns">
        <router-link to="/admin/conversations">
          <b-button variant="info" pill>
            <b-icon-arrow-bar-left />
          </b-button>
        </router-link>
        <b-button variant="info" pill @click="toggleConverationInfo">
          <b-icon-info-lg v-if="!isConversationInfoOpened" />
          <b-icon-x-lg v-else />
        </b-button>
      </div>
      <div
        v-show="getReady"
        class="admin-conversation-detail--info"
        :class="{
          'opened': conversationInfoOpened,
          'bg-light': !isDesktop,
        }"
      >
        <div class="admin-conversation-detail--info-tabs mb-3 rounded d-flex flex-column">
          <div class="admin-conversation-detail--info-tabs--header d-flex align-item-center border bg-white-custom">
            <div
              class="tab-item border border-start-0 border-bottom-0 border-top-0"
              :class="{
                'active': tabIndex === 0,
                'dark': isDarkMode,
              }"
              @click="() => { tabIndex = 0; }"
            >
              Call
            </div>
            <div
              class="tab-item border border-start-0 border-bottom-0 border-top-0"
              :class="{
                'active': tabIndex === 1,
                'dark': isDarkMode,
              }"
              @click="() => { tabIndex = 1; }"
            >
              Summary
              <b-icon
                v-if="hasData.summary"
                icon="dot"
                variant="success"
                scale="3"
                style="margin-top: -1rem; margin-left: -4px;"
              />
            </div>
            <div
              class="tab-item border border-start-0 border-bottom-0 border-top-0"
              :class="{
                'active': tabIndex === 2,
                'dark': isDarkMode,
              }"
              @click="() => { tabIndex = 2; }"
            >
              Keywords
              <b-icon
                v-if="hasData.keywords"
                icon="dot"
                variant="success"
                scale="3"
                style="margin-top: -1rem; margin-left: -4px;"
              />
            </div>
            <div
              class="tab-item"
              :class="{
                'active': tabIndex === 3,
                'dark': isDarkMode,
              }"
              @click="() => { tabIndex = 3; }"
            >
              Sentiment
              <b-icon
                v-if="hasData.sentiment"
                icon="dot"
                variant="success"
                scale="3"
                style="margin-top: -1rem; margin-left: -4px;"
              />
            </div>
          </div>
          <div class="admin-conversation-detail--info-tabs--content bg-white-custom border border-top-0">
            <AdminConversationInfo
              v-show="tabIndex == 0"
              :conversationDetail="conversationDetail"
            />
            <ConversationSummary
              :show="tabIndex == 1"
              :conversationId="conversationId"
              :conversation="conversationDetail"
              @updated="() => {
                if (!hasData.summary) {
                  tabIndex = 1;
                }
                hasData.summary = true;
              }"
            />
            <ConversationKeywords
              :show="tabIndex == 2"
              scope="admin"
              :conversationId="conversationId"
              @updated="() => { hasData.keywords = true; }"
            />
            <ConversationSentiment
              :show="tabIndex == 3"
              :conversationId="conversationId"
              @updated="() => { hasData.sentiment = true; }"
            />
          </div>
        </div>
        <AdminConversationGeography :conversationDetail="conversationDetail" />
      </div>
    </div>
  </b-overlay>
</template>

<script>
/* eslint-disable */
import { mapGetters, mapActions } from "vuex";
import AdminConversationChatHistory from "@/components/admin/conversations/AdminConversationChatHistory";
import AdminConversationInfo from "@/components/admin/conversations/AdminConversationInfo";
import AdminConversationGeography from "@/components/admin/conversations/AdminConversationGeography";
import ConversationSentiment from "@/components/conversations/ConversationSentiment";
import ConversationKeywords from "@/components/conversations/ConversationKeywords";
import ConversationSummary from "@/components/conversations/ConversationSummary";

export default {
  name: "AdminConversationDetail",
  components: {
    AdminConversationChatHistory,
    AdminConversationInfo,
    AdminConversationGeography,
    ConversationSentiment,
    ConversationKeywords,
    ConversationSummary,
  },
  computed: {
    ...mapGetters("agency", ["currentAgency", "agencySettings"]),
    ...mapGetters("user", ["currentToken", "viewport", "isDarkMode"]),
    internationalDialCodeEnabled() {
      return this.agencySettings.enableInternational == "true";
    },
    mediaTabTitle() {
      const mediaCount = this.conversationAttachments.length;
      return `Media (${mediaCount})`;
    },
    conversationInfoOpened() {
      if (this.isDesktop) {
        return true;
      } else {
        return this.isConversationInfoOpened;
      }
    },
    getReady() {
      return !this.loadingConvo && !this.loadingAttachments && !this.loadingMessages;
    },
    conversationId() {
      return this.$route.params.conversationId;
    },
  },
  data() {
    return {
      conversationDetail: {},
      conversationMessages: [],
      conversationAttachments: [],
      loadingConvo: false,
      loadingMessages: false,
      loadingAttachments: false,
      isDesktop: true,
      isConversationInfoOpened: false,
      tabIndex: 0,
      hasData: {
        summary: false,
        keywords: false,
        sentiment: false,
      },
      fetchAbortController: null,
      hasAccess: true,
    };
  },
  created() {
    this.tabIndex = 0;
    this.fetchAbortController = new AbortController();
    this.loadConversation(this.conversationId);
    this.loadConversationAttachments(this.conversationId);
  },
  methods: {
    ...mapActions("conversations", ["loadAttachments"]),
    getMessageAttachments(messageId) {
      return this.conversationAttachments
        .sort((a, b) => a.created_at - b.created_at)
        .filter(attachment => attachment.message_id == messageId)
        .map(attachment => ({
          ...attachment,
          isMedia: this.getAttachmentType(attachment.content_type) !== "image",
          isImage: this.getAttachmentType(attachment.content_type) === "image",
        }));
    },
    getAttachmentType(contentType) {
      if (contentType) {
        return contentType.includes("image") ? "image" : "media";
      }
      return "unkonwn";
    },
    async loadConversationAttachments(conversationId) {
      try {
        this.loadingAttachments = true;
        const resp = await this.$ConveyApi.attachmentsApi.getAttachments({
          params: { conversationId },
          signal: this.fetchAbortController.signal,
        });
        this.conversationAttachments = resp.data.data;
        this.loadConversationMessages(this.conversationId);
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingAttachments = false;
      }
    },
    async loadConversation(id) {
      try {
        this.loadingConvo = true;
        const resp = await this.$ConveyApi.conversationsApi.getConversation({
          conversationId: id,
          signal: this.fetchAbortController.signal,
        });
        const { status: conversationStatus } = resp.data.data;
        if (conversationStatus !== "active" && this.agencySettings['core:communicatorEnabled']) {
          this.conversationDetail = resp.data.data;
          this.$store.commit("conversations/setConversation", resp.data.data);
        } else {
          this.hasAccess = false;
          this.abortFetchOperations();
          if (this.agencySettings['core:communicatorEnabled']) {
            if (confirm(`The given conversation is ${conversationStatus}.\nWill you check the conversation in ConveyCommunicator?`)) {
              this.$router.push({ path: `/conversations/${id}` }).catch(() => {});
            } else {
              this.$router.push({ name: "Admin Dashboard" }).catch(() => {});
            }
          } else {
            if (confirm(`The given conversation is ${conversationStatus}.\nYou don't have access to check the conversation`)) {
              this.$router.push({ name: "Admin Dashboard" }).catch(() => {});
            } else {
              this.$router.push({ name: "Admin Dashboard" }).catch(() => {});
            }
          }
        }
      } catch(err) {
        console.log(err);
        this.hasAccess = false;
      } finally {
        this.loadingConvo = false;
      }
    },
    async loadConversationMessages(id) {
      try {
        this.loadingMessages = true;
        const resp = await this.$ConveyApi.messagesApi.getMessagesForConversation({
          conversationId: id,
          signal: this.fetchAbortController.signal,
        });
        this.conversationMessages = resp.data.data
          .filter((value) => value.source !==  'transcribe')
          .map(message => {
            const messageAttachments = this.getMessageAttachments(message.message_id);
            return {
              ...message,
              messageAttachments,
              num_media: messageAttachments.length || 0,
              imageAttachmentCount: messageAttachments && messageAttachments.length > 0
                ? messageAttachments.filter(attachment => attachment.isImage).length
                : 0,
              mediaAttachmentCount: messageAttachments && messageAttachments.length > 0 ?
                messageAttachments.filter(attachment => attachment.isMedia).length
                : 0,
            };
          });
      } catch(err) {
        console.log(err);
      } finally {
        this.loadingMessages = false;
      }
    },
    toggleConverationInfo() {
      this.isConversationInfoOpened = !this.isConversationInfoOpened;
    },
    abortFetchOperations() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    }
  },
  watch: {
    viewport: {
      handler(value) {
        const { innerWidth } = value;
        this.isDesktop = innerWidth > 1199;
      },
      deep: true,
      immediate: true,
    },
  },
  beforeDestroy() {
    this.abortFetchOperations();
  }
};
</script>

<style lang="scss">
.admin-conversation-detail-wrapper {
  width: 100%;
  height: 100%;
}
.admin-conversation-detail {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr min(600px, max(400px, 33%));
  gap: 1rem;

  &--info {
    opacity: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    transition: opacity ease-in-out 0.15s;

    &.opened {
      opacity: 1;
    }

    &-tabs {
      width: 100%;;
      border-radius: 1rem;

      &--header {
        width: 100%;
        height: 40px;
        border-radius: 1rem 1rem 0 0;
        .tab-item {
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          width: 25%;
          height: 100%;
          padding: 8px;
          font-weight: 500;
          opacity: 1;
          &:first-child {
            border-radius: 1rem 0 0 !important;
          }
          &:last-child {
            border-radius: 0 1rem 0  0 !important;
          }

          &.active {
            background-color: #e9edf2 !important;
            &.dark {
              background-color: #545b62 !important;
            }
          }
          
          &:hover {
            opacity: 0.7;
            background-color: #e9edf2 !important;
            &.dark {
              background-color: #545b62 !important;
            }
          }
          @media (max-width: 400px) {
            font-size: 13px;
          }
          @media (max-width: 375px) {
            font-size: 11px;
          }
        }
      }

      &--content {
        width: 100%;
        height: fit-content;
        max-height: calc(100% - 40px);
        overflow-y: auto;
        border-radius: 0 0 1rem 1rem;
      }
    }
  }

  &--chat {
    height: 100%;
    overflow-y: auto;
  }

  &.optimized-layout {
    position: relative;
    display: block;

    .admin-conversation-detail--chat {
      position: relative;
      z-index: 2;
    }

    .admin-conversation-detail--info {
      position: absolute;
      top: 0;
      right: 0;
      z-index: 1;

      &.opened {
        z-index: 3;
      }
    }

    .admin-conversation-detail--mobile-btns {
      position: absolute;
      z-index: 4;
      top: 16px;
      right: 16px;
      display: flex;
      align-items: center;
      gap: 8px;

      button {
        background-color: rgba(24, 109, 222, 0.5) !important;
      }
    }
  }
}
</style>
